<template>
  <div class="bkash-page">
    <!-- Main card -->
    <div class="bkash-page__card">
      <!-- Locale bar -->
      <div class="bkash-page__locale-bar">
        <div ref="localeMenu" class="bkash-page__locale" @click="toggleLocaleMenu">
          <img class="bkash-page__locale-icon" :src="localeIcon" alt="" />
          <span class="bkash-page__locale-text">{{ currentLocale.toUpperCase() }}</span>
          <svg
            class="bkash-page__locale-chevron"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
          <div v-if="localeMenuOpen" class="bkash-page__locale-dropdown">
            <div
              v-for="locale in locales"
              :key="locale"
              class="bkash-page__locale-item"
              :class="{ 'bkash-page__locale-item--active': locale === currentLocale }"
              @click.stop="selectLocale(locale)">
              {{ locale.toUpperCase() }}
            </div>
          </div>
        </div>
      </div>

      <!-- Header -->
      <div class="bkash-page__header">
        <div class="bkash-page__logo">
          <img :src="bkashTextIcon" alt="bKash" />
          <img :src="birdIcon" alt="" class="bkash-page__logo-bird" />
        </div>
        <div class="bkash-page__header-divider"></div>
        <div class="bkash-page__header-info">
          <div class="bkash-page__header-info-icon">i</div>
          <span class="bkash-page__header-info-text">{{ $t('bkash__how_to_pay') }}</span>
        </div>
      </div>

      <!-- Timer -->
      <div class="bkash-page__timer-strip">
        <span class="bkash-page__timer-label">{{ $t('bkash__make_payment_first') }}</span>
        <div class="bkash-page__timer-badge">
          {{ formattedTime }}
        </div>
      </div>

      <!-- Step 1: Amount -->
      <div class="bkash-page__field">
        <div class="bkash-page__field-left">
          <p class="bkash-page__field-label">{{ $t('bkash__amount_label') }}</p>
          <span class="bkash-page__field-value">{{ templateData.formattedAmount }}</span>
        </div>
        <ButtonCopy :value="templateData.formattedAmount" />
      </div>

      <!-- Step 2 instruction -->
      <div class="bkash-page__instruction">
        <div class="bkash-page__step-badge">2</div>
        <span class="bkash-page__instruction-text">{{ $t('bkash__step_copy_agent') }}</span>
      </div>

      <!-- Agent number field -->
      <div class="bkash-page__field">
        <div class="bkash-page__field-left">
          <p class="bkash-page__field-label">{{ $t('bkash__agent_number_label') }}</p>
          <span class="bkash-page__agent-number">{{ templateData.agentNumber }}</span>
        </div>
        <div class="bkash-page__field-row">
          <div class="bkash-page__field-actions">
            <ButtonCopy :value="templateData.agentNumber" />
          </div>

          <ElTooltip
            :content="$t('bkash__agent_tooltip')"
            :placement="tooltipPlacement"
            popper-class="bkash-tooltip"
            effect="light">
            <div class="bkash-page__info-icon">i</div>
          </ElTooltip>
        </div>
      </div>

      <!-- Steps 3–5 -->
      <div v-for="(stepKey, i) in remainingSteps" :key="stepKey" class="bkash-page__instruction">
        <div class="bkash-page__step-badge">{{ i + 3 }}</div>
        <span class="bkash-page__instruction-text">{{ $t(stepKey) }}</span>
      </div>

      <!-- Transaction ID input -->
      <div class="bkash-page__trx-field">
        <div class="bkash-page__trx-wrap">
          <input
            v-model="transaction_id"
            class="bkash-page__trx-input"
            :class="{ 'bkash-page__trx-input--error': trxError }"
            type="text"
            :placeholder="$t('bkash__trx_placeholder')"
            :disabled="isExpired || loading"
            autocomplete="off"
            @input="onTrxInput"
            @blur="validateTrxId" />

          <ElTooltip
            :content="$t('bkash__trx_tooltip')"
            :placement="tooltipPlacement"
            popper-class="bkash-tooltip"
            effect="light">
            <div class="bkash-page__info-icon bkash-page__info-icon--trx">i</div>
          </ElTooltip>
        </div>
        <div v-if="trxError" class="bkash-page__trx-error">
          {{ $t('bkash__trx_error') }}
        </div>
      </div>

      <button
        class="bkash-page__confirm-btn"
        type="button"
        :disabled="loading || !transaction_id.trim() || trxError"
        @click="isExpired ? handleTryAgain() : confirm()">
        <Loader v-if="loading" />
        <span v-else>{{ $t('bkash__confirm') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ButtonCopy from './components/ButtonCopy.vue';
import Loader from './components/Loader.vue';
import BirdIcon from './assets/bird.svg';
import BkashTextIcon from './assets/bKash.svg';
import LocaleIcon from './assets/locale.svg';
import { createDayJsDurationFormatterFactory } from '@/helpers/DayJsDurationFormatter/DayJsDurationFormatterImpl';
import { MoneyFormatter } from '@/helpers/Formatting/MoneyFormatter';
import { messages } from './translations/index';
import parseAvailableLocales from '@/helpers/parseAvailableLocales';

export default {
  components: { ButtonCopy, Loader },

  created() {
    this.startTimer(this.templateData.expires);

    if (this.locales.includes(this.selectedLocale)) {
      this.selectLocale(this.selectedLocale);
    } else {
      this.selectLocale(this.locales.length ? this.locales[0] : 'en');
    }

    document.addEventListener('click', this.closeLocaleMenu);
    window.addEventListener('resize', this.onResize);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeLocaleMenu);
    window.removeEventListener('resize', this.onResize);
  },

  data() {
    return {
      birdIcon: BirdIcon,
      bkashTextIcon: BkashTextIcon,
      localeIcon: LocaleIcon,
      remainingSteps: [
        'bkash__step_open_app',
        'bkash__step_paste_account',
        'bkash__step_enter_trx',
      ],
      loading: false,
      transaction_id: '',
      trxError: false,
      localeMenuOpen: false,
      howToPayOpen: false,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData', 'flowData', 'checkoutOptions']),
    ...mapState('InvoiceLifetime', ['isExpired', 'timeToExpired']),
    ...mapState('UI', ['availableLocales', 'queryLocale']),

    locales() {
      const optionLocales = parseAvailableLocales(
        this.availableLocales,
        this.checkoutOptions?.available_locales,
        Object.keys(messages)
      );
      return optionLocales.length ? optionLocales : Object.keys(messages);
    },

    selectedLocale() {
      const { default_locale } = this.paymentInvoice;
      const supportedLocale = locale =>
        Object.keys(messages).some(key => key === locale) ? locale : null;
      return (
        supportedLocale(default_locale) ||
        supportedLocale(this.queryLocale) ||
        supportedLocale(this.checkoutOptions?.locale) ||
        supportedLocale(this.$browserLocale)
      );
    },

    currentLocale() {
      return this.$i18n.locale;
    },

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    dateFormatter() {
      return createDayJsDurationFormatterFactory({ defaultTemplate: 'HH:mm:ss' });
    },

    formattedTime() {
      return this.dateFormatter().format(this.timeToExpired);
    },

    templateData() {
      const { has_return_url, id, expires } = this.paymentInvoice;
      const { amount: mainAmount, currency: mainCurrency } = this.flowData || {};
      const { requisites, method, amount: payloadAmount, currency: payloadCurrency } = this.flowData?.payload || {};

      const parseRequisites = () => {
        if (!requisites) return {};
        if (typeof requisites === 'string') {
          try {
            return JSON.parse(requisites);
          } catch {
            return {};
          }
        }
        return requisites;
      };

      const { account_number = '' } = parseRequisites();
      const amount = [undefined, null].includes(payloadAmount) ? mainAmount : payloadAmount;
      const currency = [undefined, null].includes(payloadCurrency) ? mainCurrency : payloadCurrency;
      const formattedAmount = amount != null ? new MoneyFormatter().format(amount, currency, false) : '';

      return {
        id,
        has_return_url,
        expires,
        logo: (method || {}).logo || null,
        formattedAmount,
        agentNumber: account_number,
      };
    },

    tooltipPlacement() {
      return this.windowWidth <= 520 ? 'top' : 'bottom';
    },
  },

  watch: {
    isExpired(val) {
      if (val && !this.loading && !this.transaction_id.trim()) {
        this.$router.push({
          name: 'PaymentStatus',
          params: { id: this.paymentInvoice.id },
        });
      }
    },
  },

  methods: {
    ...mapActions(['confirmPaymentInvoice']),
    ...mapActions('InvoiceLifetime', ['startTimer']),

    onResize() {
      this.windowWidth = window.innerWidth;
    },

    selectLocale(locale) {
      this.localeMenuOpen = false;
      this.$i18n.locale = locale;
      this.$bus.$emit('changeLocale', locale);
    },

    toggleLocaleMenu() {
      this.localeMenuOpen = !this.localeMenuOpen;
    },

    closeLocaleMenu(event) {
      if (!this.$refs.localeMenu || !this.$refs.localeMenu.contains(event.target)) {
        this.localeMenuOpen = false;
      }
    },

    onTrxInput() {
      this.transaction_id = this.transaction_id.replace(/[^a-zA-Z0-9]/g, '');
      this.trxError = false;
    },

    validateTrxId() {
      if (!this.transaction_id) {
        this.trxError = false;
        return true;
      }
      this.trxError = !/^[a-zA-Z0-9]+$/.test(this.transaction_id);
      return !this.trxError;
    },

    async confirm() {
      if (this.loading || !this.transaction_id.trim() || !this.validateTrxId()) return;

      this.loading = true;
      const { status } = await this.confirmPaymentInvoice({
        id: this.templateData.id,
        data: { payload: { transaction_id: this.transaction_id.trim() } },
      });

      if (status !== 200) {
        this.loading = false;
      }
    },

    handleTryAgain() {
      if (!this.templateData.has_return_url) return;
      location.replace(`/return?id=${this.paymentInvoice.id}&force=true`);
    },
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

$bkash-primary: #b1245b;
$bkash-primary-dark: #b50d55;
$bkash-card-bg: #ecebea;
$bkash-field-bg: #d9d8da;
$bkash-text: #1a1a1a;
$bkash-label: #959395;
$bkash-text-muted: #757575;
$bkash-agent-color: #2f2e8d;
$bkash-border: #e0e0e0;
$bkash-field-light: #fdfcfe;

.bkash-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  padding: 12px 16px 32px;
  font-family: 'Inter', sans-serif;
  box-sizing: border-box;

  &__card {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 520px;
    background: $bkash-card-bg;
    border-radius: 24px;
    padding: 16px;
    overflow: hidden;
    margin-top: 15%;
  }

  &__info-icon {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: $bkash-text;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Georgia, serif;
    font-style: italic;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
    line-height: 1;
  }

  &__locale-bar {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    max-width: 520px;
    margin: 20px 20px 20px -10px;
  }

  &__locale {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    cursor: pointer;
    user-select: none;
    border-radius: 6px;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  }

  &__locale-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &__locale-text {
    font-size: 13px;
    font-weight: 600;
    color: $bkash-primary;
    letter-spacing: 0.02em;
  }

  &__locale-chevron {
    flex-shrink: 0;
    color: $bkash-primary;
  }

  &__locale-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    min-width: 72px;
    background: $bkash-card-bg;
    border: 1px solid $bkash-border;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    z-index: 200;
    overflow: hidden;
  }

  &__locale-item {
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 600;
    color: #5f5f5f;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background: #f5f5f5;
    }

    &--active {
      color: $bkash-primary;
      background: rgba(226, 19, 110, 0.06);
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 16px 20px;
    border: 1px solid $bkash-border;
    border-radius: 16px;
    background: $bkash-field-light;
    min-height: 72px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04);
  }

  &__logo {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 40px;

    img {
      max-height: 40px;
      object-fit: contain;
    }
  }

  &__logo-bird {
    max-height: 36px;
  }

  &__header-divider {
    width: 2px;
    align-self: stretch;
    background: $bkash-border;
    margin: -16px 16px;
    flex-shrink: 0;
  }

  &__logo-placeholder {
    font-size: 22px;
    font-weight: 700;
    color: $bkash-primary;
    letter-spacing: -0.5px;
  }

  &__header-info {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 12px;
  }

  &__header-info-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: $bkash-primary;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Georgia, serif;
    font-style: italic;
    font-size: 18px;
    font-weight: 700;
    flex-shrink: 0;
    line-height: 1;
  }

  &__header-info-text {
    font-size: 12px;
    font-weight: 500;
    color: $bkash-text;
    line-height: 1.4;
    max-width: 90px;
  }

  &__timer-strip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    background: $bkash-field-light;
    border-radius: 24px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04);
  }

  &__timer-label {
    font-size: 13px;
    font-weight: 600;
    color: $bkash-text;
    font-family: 'Inter', sans-serif;
  }

  &__timer-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 54px;
    height: 28px;
    padding: 4px 10px;
    background: $bkash-primary;
    border-radius: 100px;
    font-size: 12px;
    font-weight: 500;
    color: #ffffff;
    letter-spacing: 0.03em;
    font-variant-numeric: tabular-nums;
  }

  &__field {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding: 8px 24px;
    background: $bkash-field-bg;
    border-radius: 24px;
  }

  &__field-left {
    text-align: start;
  }

  &__field-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__field-label {
    font-size: 11px;
    font-weight: 600;
    color: $bkash-label;
    margin: 0 0 2px 0;
  }

  &__field-value {
    font-size: 16px;
    font-weight: 700;
    color: $bkash-text;
    letter-spacing: -0.02em;
  }

  &__field-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__agent-number {
    font-size: 16px;
    font-weight: 700;
    color: $bkash-agent-color;
    text-decoration: underline;
  }

  &__instruction {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
  }

  &__step-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #ffffff;
    color: $bkash-text;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
    margin-top: 1px;
  }

  &__instruction-text {
    font-size: 12px;
    font-weight: 500;
    color: $bkash-text;
    line-height: 1.5;
    text-align: left;
  }

  &__trx-field {
    margin-top: 16px;
  }

  &__trx-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__info-icon--trx {
    position: absolute;
    right: 26px;
    background: $bkash-text;
  }

  &__trx-input {
    width: 100%;
    padding: 16px 48px 16px 20px;
    background: $bkash-field-light;
    border: 1.5px solid transparent;
    border-radius: 24px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: $bkash-text;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s;

    &::placeholder {
      color: $bkash-text-muted;
    }

    &:focus {
      border-color: $bkash-primary;
      background: #fff;
    }

    &--error {
      border-color: #d32f2f !important;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__trx-error {
    margin-top: 6px;
    padding: 0 4px;
    font-size: 12px;
    font-weight: 500;
    color: #d32f2f;
  }

  &__confirm-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    margin: 24px auto;
    padding: 15px;
    background: $bkash-primary;
    border: none;
    border-radius: 24px;
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.2s;
    letter-spacing: 0.01em;

    &:hover:not(:disabled) {
      background: $bkash-primary-dark;
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  @media (max-width: 520px) {
    background: #ecebea;
    padding: 0 16px 32px;
    justify-content: flex-start;

    &__card {
      background: transparent;
      border-radius: 0;
      box-shadow: none;
      padding: 0;
      overflow: visible;
      margin-top: 0;
    }
  }
}

.bkash-tooltip {
  @media (max-width: 520px) {
    max-width: calc(100vw - 32px) !important;
    white-space: normal !important;
    word-break: break-word;
  }
}
</style>
